import { Injectable } from '@angular/core';
import { forkJoin, Observable, lastValueFrom } from 'rxjs';
import { WebService } from './web.service';
import { map } from 'rxjs/operators';

type DataKey =
  | 'Products'
  | 'PaymentTerms'
  | 'SampleApprovalTerms'
  | 'WeightTypes'
  | 'ShipmentTerms'
  | 'ShipmentTypes'
  | 'FuturesMonths'
  | 'DestinationWarehouses'
  | 'DestinationPorts'
  | 'PriceDescriptors'
  | 'OriginRates'
  | 'Costings'
  | 'Decaffeinators'
  | 'OriginPorts'
  | 'ArrivalPorts'
  | 'ArrivalTerminals'
  | 'Truckers'
  | 'InboundWeighingOptions'
  | 'InboundUnloadingOptions'
  | 'Suppliers'
  | 'DrayageCarriers'
  | 'Countries'
  | 'Regions'
  | 'SubRegions'
  | 'Genes'
  | 'Traits'
  | 'Groups'
  | 'CurrentNYCBasis'
  | 'CurrentLDNBasis';

type GetOptions = {
  keys: 'ALL' | DataKey[];
  exclude?: DataKey[]; // used only when keys === 'ALL'
  params?: Partial<Record<DataKey, Record<string, string | number | boolean>>>;
  includeStatic?: boolean;
  turnOffLoader?: boolean; // optional flag to turn off loader for this request
};

@Injectable({ providedIn: 'root' })
export class InventoryDataService {
  TurnOffLoader = false; // default value
  constructor(private web: WebService) { }

  private sources: Record<DataKey, (params?: Record<string, any>) => Observable<any>> = {
    Products: () => this.web.getWebCall('inventory', 'coffee/products', {skipLoader: this.TurnOffLoader }),
    PaymentTerms: () => this.web.getWebCall('data', 'coffee/payment-terms', {skipLoader: this.TurnOffLoader }),
    SampleApprovalTerms: () => this.web.getWebCall('data', 'coffee/sample-approval-terms', {skipLoader: this.TurnOffLoader }),
    WeightTypes: () => this.web.getWebCall('data', 'coffee/weight-types', {skipLoader: this.TurnOffLoader }),
    ShipmentTerms: () => this.web.getWebCall('data', 'coffee/shipment-terms', {skipLoader: this.TurnOffLoader }),
    ShipmentTypes: () => this.web.getWebCall('data', 'coffee/shipment-types', {skipLoader: this.TurnOffLoader }),
    FuturesMonths: () => this.web.getWebCall('inventory', 'coffee/risk-board/futures', {skipLoader: this.TurnOffLoader }),
    DestinationWarehouses: () => this.web.getWebCall('data', 'coffee/locations', {skipLoader: this.TurnOffLoader }),
    DestinationPorts: () => this.web.getWebCall('data', 'coffee/destinations', {skipLoader: this.TurnOffLoader }),
    PriceDescriptors: () => this.web.getWebCall('data', 'coffee/price-descriptors', {skipLoader: this.TurnOffLoader }),
    OriginRates: () => this.web.getWebCall('data', 'coffee/origin-rates', {skipLoader: this.TurnOffLoader }),
    Costings: () => this.web.getWebCall('data', 'coffee/costings', {skipLoader: this.TurnOffLoader }),
    Decaffeinators: () => this.web.getWebCall('data', 'coffee/decaffeinators', {skipLoader: this.TurnOffLoader }),
    OriginPorts: () => this.web.getWebCall('data', 'coffee/origin-ports', {skipLoader: this.TurnOffLoader }),
    ArrivalPorts: () => this.web.getWebCall('data', 'coffee/entry-ports', {skipLoader: this.TurnOffLoader }),
    ArrivalTerminals: () => this.web.getWebCall('data', 'coffee/entry-terminals', {skipLoader: this.TurnOffLoader }),
    Truckers: (p) => this.web.getWebCall('data', 'coffee/truckers', { ...p, skipLoader: this.TurnOffLoader }),
    InboundWeighingOptions: () => this.web.getWebCall('data', 'coffee/inbound-weighing-options', {skipLoader: this.TurnOffLoader }),
    InboundUnloadingOptions: () => this.web.getWebCall('data', 'coffee/inbound-unloading-options', {skipLoader: this.TurnOffLoader }),
    Suppliers: () => this.web.getWebCall('inventory', '/coffee/suppliers', {skipLoader: this.TurnOffLoader }),
    DrayageCarriers: () => this.web.getWebCall('data', '/coffee/drayage-carriers', {skipLoader: this.TurnOffLoader }),
    Countries: () => this.web.getWebCall('data', '/coffee/countries', {skipLoader: this.TurnOffLoader }),
    Regions: () => this.web.getWebCall('data', '/coffee/regions', {skipLoader: this.TurnOffLoader }),
    SubRegions: () => this.web.getWebCall('data', '/coffee/sub-regions', {skipLoader: this.TurnOffLoader }),
    Genes: () => this.web.getWebCall('data', '/coffee/genes', {skipLoader: this.TurnOffLoader }),
    Traits: () => this.web.getWebCall('data', '/coffee/traits', {skipLoader: this.TurnOffLoader }),
    Groups: () => this.web.getWebCall('data', '/coffee/groups', {skipLoader: this.TurnOffLoader }),
    CurrentNYCBasis: () => this.web.getWebCall('data', '/coffee/current-nyc-basis', {skipLoader: this.TurnOffLoader }),
    CurrentLDNBasis: () => this.web.getWebCall('data', '/coffee/current-ldn-basis', {skipLoader: this.TurnOffLoader }),
  };

  /** Selectively (or fully) fetch data */
  getData(options: GetOptions) {
    const { keys, exclude = [], params, includeStatic = true } = options;
    this.TurnOffLoader = options.turnOffLoader ?? false; // Set the flag based on options
    // Determine which keys to fetch
    const resolvedKeys: DataKey[] =
      keys === 'ALL'
        ? (Object.keys(this.sources) as DataKey[]).filter(k => !exclude.includes(k))
        : keys;

    // Build the forkJoin input from resolvedKeys
    const observables = resolvedKeys.reduce((acc, key) => {
      const src = this.sources[key];
      acc[key] = src(params?.[key]);
      return acc;
    }, {} as Record<string, Observable<any>>);

    // Fetch in parallel; consumers can map the result as needed

    const joined$ = forkJoin(observables).pipe(
      map(result => {
        if (includeStatic) {
          return {
            ...result,
            Months: this.getMonths(),
            Years: this.getYears(),
          };
        }
        return result;
      })
    );


    // If you want to inject Months/Years here, pipe & map the result.
    // For brevity, we return the raw joined$ and let the component decide.
    return lastValueFrom(joined$);
  }

  private getMonths() {
    return [
      { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
      { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
      { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
      { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
    ];
  }

  private getYears() {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => {
      const year = currentYear + i;
      return { label: year.toString(), value: year };
    });
  }
}
